// app/frontend/src/pages/InitiativesPage.jsx
import React, { useMemo, useState } from "react";

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
      <h3 className="text-base md:text-lg font-semibold text-white">{title}</h3>
      <div className="mt-3 text-sm md:text-[15px] leading-6 text-white/80">
        {children}
      </div>
    </section>
  );
}

function Pill({ children, tone = "neutral" }) {
  const toneClass =
    tone === "always"
      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/25"
      : tone === "situational"
        ? "bg-amber-500/15 text-amber-200 border-amber-500/25"
        : "bg-white/10 text-white/80 border-white/10";

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${toneClass}`}
    >
      {children}
    </span>
  );
}

function List({ items }) {
  return (
    <ul className="mt-3 list-disc pl-5 space-y-1 text-white/80">
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ul>
  );
}

const TABS = [
  { id: "skills", label: "Future-Safe Skills" },
  { id: "parents", label: "Single Parent Support" },
  { id: "housing", label: "Community Housing" },
];

export default function InitiativesPage() {
  const [active, setActive] = useState("skills");

  const tab = useMemo(() => {
    switch (active) {
      case "skills":
        return SkillsTab;
      case "parents":
        return ParentsTab;
      case "housing":
        return HousingTab;
      default:
        return SkillsTab;
    }
  }, [active]);

  const ActiveTab = tab;

  return (
    <div className="min-h-screen bg-black">
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-4xl font-bold text-white">
            Community Initiatives
          </h1>
          <p className="text-sm md:text-base text-white/70 max-w-3xl">
            These initiatives live quietly by design. Some components are always
            active. Others are situational—dependent on partnerships, capacity,
            and operational readiness. Platform engagement helps accelerate what
            becomes possible over time, but Unity Love never treats support as
            guaranteed or immediate.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Pill tone="always">Always-On = Commitment</Pill>
            <Pill tone="situational">Situational = Capacity</Pill>
            <Pill>Non-cash delivery (vouchers/credits)</Pill>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8">
          <div className="flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-white/5 p-2">
            {TABS.map((t) => {
              const isActive = t.id === active;
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={[
                    "px-4 py-2 rounded-xl text-sm transition-colors",
                    isActive
                      ? "bg-white text-black"
                      : "text-white/80 hover:text-white hover:bg-white/10",
                  ].join(" ")}
                  aria-current={isActive ? "page" : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <ActiveTab />
          </div>
        </div>
      </div>
    </div>
  );
}

/** TAB 1 */
function SkillsTab() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Section title="What this is">
        Unity Love quietly supports <b>future-safe skill development</b> for
        people in our community—practical paths that remain valuable as
        technology evolves. This is not a charity campaign. It&apos;s a long-term
        community commitment.
      </Section>

      <Section title="Why it's here">
        <List
          items={[
            "Automation and AI are reshaping the workforce",
            "Adults need faster, practical paths to stability",
            "Education should unlock momentum, not debt",
            "Opportunity should be quiet, dignified, and real",
          ]}
        />
      </Section>

      <Section title="Skill paths we fund">
        <div className="flex flex-wrap gap-2">
          <Pill>Smart Trades & Infrastructure</Pill>
          <Pill>Human-Centered Healthcare Support</Pill>
          <Pill>IT Support, Systems & Cyber Hygiene</Pill>
          <Pill>AI, Automation & Robotics Support</Pill>
          <Pill>Operations, Coordination & Human Judgment</Pill>
          <Pill>Hybrid Skill Stacks</Pill>
        </div>

        <p className="mt-3">
          We focus on skills that are hard to automate end-to-end, strengthened
          by technology, and learnable in months—then stackable over years.
        </p>
      </Section>

      <Section title="How it works">
        <List
          items={[
            "The fund accumulates quietly through normal platform activity",
            "Award cycles happen periodically (as capacity allows)",
            "Eligible members can apply or be nominated",
            "Support is provided via vouchers/credits/program access—not cash",
            "We track outcomes to stay accountable and improve over time",
          ]}
        />
      </Section>

      <Section title="Always-On vs Situational">
        <div className="mt-2 flex flex-wrap gap-2">
          <Pill tone="always">Fund accumulation</Pill>
          <Pill tone="always">Skill track framework</Pill>
          <Pill tone="always">Voucher-based delivery</Pill>
          <Pill tone="situational">Award cycles</Pill>
          <Pill tone="situational">Number of recipients</Pill>
          <Pill tone="situational">Voucher tiers</Pill>
        </div>
      </Section>

      <Section title="Why these were chosen">
        These paths share core traits: real employment outcomes, human judgment
        or physical presence, and long-term relevance as technology evolves.
        We&apos;re not chasing trends—we&apos;re building resilience.
      </Section>
    </div>
  );
}

/** TAB 2 */
function ParentsTab() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Section title="What this is">
        The Single Parent Support & Family Stability Initiative provides{" "}
        <b>practical, immediate assistance</b> that reduces stress and increases
        stability for single parents and their children—without stigma or
        spectacle.
      </Section>

      <Section title="What we support">
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-2">
              <Pill tone="always">Always-On</Pill>
              <span className="text-white font-semibold">
                School supplies & education essentials
              </span>
            </div>
            <p className="mt-1 text-white/75">
              Backpacks, supply kits, learning materials, and essentials that
              keep kids prepared and reduce household pressure.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Pill tone="always">Always-On</Pill>
              <span className="text-white font-semibold">
                Food & household stability
              </span>
            </div>
            <p className="mt-1 text-white/75">
              Grocery and household essential support designed to stabilize
              families during tight windows—not replace long-term income.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Pill tone="situational">Situational</Pill>
              <span className="text-white font-semibold">
                Transportation & access <span className="text-white/60">(Coming soon / when ready)</span>
              </span>
            </div>
            <p className="mt-1 text-white/75">
              Targeted support that removes short-term barriers to work, school,
              or training. Availability depends on verified need, geographic
              feasibility, and overall program capacity.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Pill tone="situational">Situational</Pill>
              <span className="text-white font-semibold">
                Childcare assistance <span className="text-white/60">(Coming soon / when ready)</span>
              </span>
            </div>
            <p className="mt-1 text-white/75">
              Time-bound support during defined periods (training, exams,
              interviews, work transitions). Activates only when partnerships,
              funding capacity, and operational readiness align.
            </p>
          </div>
        </div>
      </Section>

      <Section title="How it's funded (no numbers)">
        This initiative is supported passively through normal platform activity
        and partnerships. There are no public donation campaigns or
        pressure-driven appeals. Participation is never required.
      </Section>

      <Section title="How it works">
        <List
          items={[
            "Support cycles are announced periodically",
            "Eligible parents can apply or be nominated",
            "Requests focus on immediate, verifiable needs",
            "Assistance is delivered via vouchers/credits/direct support—not cash",
            "Situational supports activate only when capacity allows",
          ]}
        />
      </Section>

      <Section title="Why we chose this approach">
        We focus on essentials because small interventions can prevent larger
        crises. The goal is stability, not dependency—and dignity always comes
        first.
      </Section>

      <Section title="Always-On vs Situational">
        <div className="mt-2 flex flex-wrap gap-2">
          <Pill tone="always">Intake readiness</Pill>
          <Pill tone="always">School supply framework</Pill>
          <Pill tone="always">Food/household partners</Pill>
          <Pill tone="situational">Back-to-school cycles</Pill>
          <Pill tone="situational">Seasonal food support</Pill>
          <Pill tone="situational">Transportation</Pill>
          <Pill tone="situational">Childcare</Pill>
        </div>
      </Section>
    </div>
  );
}

/** TAB 3 */
function HousingTab() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Section title="What this is">
        The Community Housing & Stability Initiative is Unity Love&apos;s long-term
        commitment to <b>safe, transitional housing</b>—starting with single
        homeless women and veterans. Not shelters. Not temporary fixes. Real
        places designed for stability and transition.
      </Section>

      <Section title="Who this supports">
        <List
          items={[
            "Single women experiencing housing insecurity",
            "Women exiting unsafe or unstable living situations",
            "Veterans transitioning back into civilian life",
            "Individuals actively pursuing work, education, or training",
          ]}
        />
      </Section>

      <Section title="The housing model">
        <List
          items={[
            "Purpose-built, multi-unit housing (small to mid scale)",
            "Secure access and safety controls",
            "Shared common spaces (as appropriate)",
            "Support services delivered through partners—not replacement systems",
            "Transitional stays with a clear path toward independent housing",
          ]}
        />
      </Section>

      <Section title="Grant-ready by design">
        This initiative is structured to be partnership-ready and grant-eligible
        from day one: defined population focus, measurable outcomes, physical
        infrastructure, and phased execution.
      </Section>

      <Section title="Always-On vs Situational">
        <div className="mt-2 flex flex-wrap gap-2">
          <Pill tone="always">Framework & governance</Pill>
          <Pill tone="always">Partner development</Pill>
          <Pill tone="always">Grant-readiness documentation</Pill>
          <Pill tone="situational">Property acquisition/development</Pill>
          <Pill tone="situational">Pilot launches</Pill>
          <Pill tone="situational">Resident intake periods</Pill>
          <Pill tone="situational">Expansion phases</Pill>
        </div>
      </Section>

      <Section title="How it's funded (no numbers)">
        Designed for grant and partnership alignment. Platform activity can
        contribute passively, but the initiative is built to integrate with
        municipal, state, federal, and foundation support—without competing with
        existing services.
      </Section>
    </div>
  );
}
